import { mulberry32 } from './rng';
import type { EdgeSpec, EdgeType, PieceEdges, PieceSpec, PuzzleSpec } from './types';

/** 允许的行列范围（防止性能崩溃）。 */
export const MIN_GRID = 2;
export const MAX_GRID = 20;

/**
 * 生成一整盘拼图的边规格。
 *
 * 相邻约束：任意两个相邻块共享的那条边，一边 tab、另一边 blank，
 * 且共用同一个 seed（耳朵形状一致）→ 拼合时严丝合缝。
 *
 * 内部边表约定（以"上/左"那块的视角存储）：
 * - `hEdges[r][c]`：块 (r,c) 的 right 边 = 块 (r,c+1) 的 left 边（互补）。
 * - `vEdges[r][c]`：块 (r,c) 的 bottom 边 = 块 (r+1,c) 的 top 边（互补）。
 *
 * 结果可复现：相同 (rows, cols, pieceW, pieceH, puzzleSeed) → 完全相同的 PuzzleSpec。
 */
export function generatePuzzle(
  rows: number,
  cols: number,
  pieceWidth: number,
  pieceHeight: number,
  puzzleSeed: number,
): PuzzleSpec {
  if (!Number.isInteger(rows) || !Number.isInteger(cols)) {
    throw new Error(`rows/cols 必须是整数，收到 rows=${rows} cols=${cols}`);
  }
  if (rows < MIN_GRID || rows > MAX_GRID || cols < MIN_GRID || cols > MAX_GRID) {
    throw new Error(
      `rows/cols 必须在 [${MIN_GRID}, ${MAX_GRID}]，收到 rows=${rows} cols=${cols}`,
    );
  }
  if (!(pieceWidth > 0) || !(pieceHeight > 0)) {
    throw new Error(
      `pieceWidth/pieceHeight 必须为正，收到 w=${pieceWidth} h=${pieceHeight}`,
    );
  }

  const rng = mulberry32(puzzleSeed >>> 0);

  const hEdges: EdgeSpec[][] = [];
  const vEdges: EdgeSpec[][] = [];
  for (let r = 0; r < rows; r++) {
    hEdges[r] = [];
    vEdges[r] = [];
    for (let c = 0; c < cols; c++) {
      if (c < cols - 1) hEdges[r][c] = randomEdge(rng);
      if (r < rows - 1) vEdges[r][c] = randomEdge(rng);
    }
  }

  const pieces: PieceSpec[] = [];
  let id = 0;
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      pieces.push({
        id: id++,
        row: r,
        col: c,
        edges: pieceEdgesAt(rows, cols, hEdges, vEdges, r, c),
        baseX: c * pieceWidth,
        baseY: r * pieceHeight,
      });
    }
  }

  return { rows, cols, pieceWidth, pieceHeight, puzzleSeed: puzzleSeed >>> 0, pieces };
}

function randomEdge(rng: () => number): EdgeSpec {
  const type: EdgeType = rng() < 0.5 ? 'tab' : 'blank';
  const seed = Math.floor(rng() * 0xffffffff) >>> 0;
  return { type, seed };
}

const FLAT: EdgeSpec = { type: 'flat', seed: 0 };

function complement(edge: EdgeSpec): EdgeSpec {
  if (edge.type === 'flat') return { type: 'flat', seed: edge.seed };
  return { type: edge.type === 'tab' ? 'blank' : 'tab', seed: edge.seed };
}

/** 取某块四条边的规格（应用相邻互补约束）。 */
function pieceEdgesAt(
  rows: number,
  cols: number,
  hEdges: EdgeSpec[][],
  vEdges: EdgeSpec[][],
  r: number,
  c: number,
): PieceEdges {
  const top: EdgeSpec = r === 0 ? FLAT : complement(vEdges[r - 1][c]);
  const right: EdgeSpec = c === cols - 1 ? FLAT : hEdges[r][c];
  const bottom: EdgeSpec = r === rows - 1 ? FLAT : vEdges[r][c];
  const left: EdgeSpec = c === 0 ? FLAT : complement(hEdges[r][c - 1]);
  return { top, right, bottom, left };
}
